import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable, take } from 'rxjs';
import { AppState } from '../state/appstate';
import * as groupsSelector from '../state/selectors/group.selector';
import { GroupBillingAlert } from '../state/selectors/group.selector';
import {
  DlgPaymentPendingComponent,
  PaymentPendingDialogData,
} from '../views/components/dialogs/dlg-payment-pending/dlg-payment-pending.component';

@Injectable({
  providedIn: 'root',
})
export class GroupFeatureAccessService {
  private store = inject(Store<AppState>);
  private dialog = inject(MatDialog);

  readonly selectedGroup$ = this.store.select(groupsSelector.getSelectedGroup);
  readonly selectedGroupId$ = this.store.select(groupsSelector.selectedGroupId);
  readonly billingAlert$: Observable<GroupBillingAlert> = this.store.select(
    groupsSelector.getSelectedGroupBillingAlert,
  );
  readonly isLocked$: Observable<boolean> = this.store.select(
    groupsSelector.isSelectedGroupLocked,
  );
  readonly canWrite$: Observable<boolean> = this.isLocked$.pipe(
    map((locked) => !locked),
  );

  /**
   * Central check to determine if creating/updating features is currently allowed.
   * If the group is locked due to pending payment, it optionally displays the warning dialog.
   * @param showAlertIfBlocked If true, automatically displays the dialog informing members features are disabled.
   * @returns true if allowed, false if feature access is locked.
   */
  checkActionAllowed(showAlertIfBlocked = true): boolean {
    let isLocked = false;
    let alertInfo: GroupBillingAlert | null = null;
    let group: any = null;

    combineLatest([
      this.isLocked$,
      this.billingAlert$,
      this.selectedGroup$,
    ])
      .pipe(take(1))
      .subscribe(([locked, alert, grp]) => {
        isLocked = locked;
        alertInfo = alert;
        group = grp;
      });

    if (isLocked) {
      if (showAlertIfBlocked && group?.id && alertInfo) {
        this.openPaymentDialog(group, alertInfo);
      }
      return false;
    }

    return true;
  }

  /**
   * Opens the payment pending / locked dialog for the given group.
   */
  openPaymentDialog(group: any, alertInfo: GroupBillingAlert): void {
    const isAdmin =
      group?.memberType?.includes?.('admin') ||
      group?.memberType?.includes?.('president') ||
      group?.memberType?.includes?.('principal');

    const dialogData: PaymentPendingDialogData = {
      groupId: group.id,
      groupType: group.groupType || 'school',
      stage: alertInfo.stage as any,
      failedProducts: alertInfo.failedProducts,
      daysRemaining: alertInfo.daysRemaining,
      isAdmin,
    };

    DlgPaymentPendingComponent.open(this.dialog, dialogData);
  }

  /**
   * Evaluates whether the automatic session pop-up alert should be shown upon entering group.
   * Day 7+ (alert_popup) or Day 14+ (locked). Shown once per session per stage.
   */
  checkAndPromptSessionAlert(group: any, alertInfo: GroupBillingAlert): void {
    if (!group?.id || !alertInfo?.hasAlert) return;
    if (alertInfo.stage !== 'alert_popup' && alertInfo.stage !== 'locked') return;

    const sessionKey = `atc_billing_alert_${group.id}_${alertInfo.stage}`;
    if (sessionStorage.getItem(sessionKey)) {
      return; // Already shown in this tab session
    }

    sessionStorage.setItem(sessionKey, 'shown');
    this.openPaymentDialog(group, alertInfo);
  }
}
